// Reformats the backend Risk Story narrative (ADO-24865).
//
// The backend emits one full sentence per exposed file, e.g.
//   Active (first): <actor> <verb> "<file>" (labeled <label>) thus exposing it
//     to <recipient>; this exposed-to employee is untrusted for such content
//     (<sensitivity>).
//   Passive (rest): the file "<file>" (labeled <label>) was exposed to
//     <recipient>; this exposed-to employee is untrusted for such content
//     (<sensitivity>).
//
// For a multi-file event this repeats near-identical text N times. We parse each
// sentence and regroup by recipient into one consolidated sentence: the first
// three files inline (with a "(+N files)" tail) and the deduped sensitivities.
//
// The file name, its label, and the sensitivity phrase live ONLY in this prose —
// the structured risk-story payload does not carry them — so this reformatter
// parses the sentences rather than rebuilding them from fields. If anything fails
// to parse, we return the original text unchanged: never omit a risked file and
// never emit empty/broken text.

const FILES_SHOWN = 3;

interface ParsedFile {
  file: string;
  label?: string;
  recipient: string;
  sensitivity: string;
}

// Only active sentences ("... exposing it to ...") name the actor and verb.
const isActive = (s: string): boolean => /exposing it to/.test(s);

const matchFile = (s: string): string | undefined => {
  const m = s.match(/"([^"]+)"/);
  return m ? m[1] : undefined;
};

const matchLabel = (s: string): string | undefined => {
  const m = s.match(/\(labeled ([^)]*)\)/);
  return m ? m[1].trim() : undefined;
};

const matchRecipient = (s: string): string | undefined => {
  const m = s.match(/(?:exposing it to|was exposed to) (.+?);/);
  return m ? m[1].trim() : undefined;
};

const matchSensitivity = (s: string): string | undefined => {
  const m = s.match(/untrusted for such content \(([^)]*)\)/);
  return m ? m[1].trim() : undefined;
};

// 'Cognni Automations emailed "x"' -> { actor: "Cognni Automations", verb: "emailed" }
const matchActorVerb = (
  s: string
): { actor: string; verb: string } | undefined => {
  const m = s.match(/^(.+?) (\w+) "/);
  return m ? { actor: m[1].trim(), verb: m[2] } : undefined;
};

// Split a single joined story string into sentences on the ". " boundary that
// follows a closing paren. We mark the boundary with a newline (which never
// appears in the prose) and split on that, so each sentence keeps its trailing
// ")" — needed to parse the sensitivity. A newline delimiter avoids a lookbehind,
// which is unsupported on older Safari.
const splitSentences = (text: string): string[] =>
  text
    .replace(/\)\.\s+/g, ").\n")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

// "a" | "a and b" | "a, b, and c" (Oxford comma for 3+).
const oxfordJoin = (parts: string[]): string => {
  if (parts.length <= 1) return parts[0] || "";
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
};

const fileClause = (f: ParsedFile): string =>
  f.label ? `"${f.file}" (labeled ${f.label})` : `"${f.file}"`;

const buildGroupSentence = (
  actor: string,
  verb: string,
  recipient: string,
  files: ParsedFile[]
): string => {
  const shown = files.slice(0, FILES_SHOWN);
  const noun = files.length === 1 ? "the file" : "the files";
  const fileList = oxfordJoin(shown.map(fileClause));
  const extra = files.length - shown.length;
  const extraTail = extra > 0 ? ` (+${extra} files)` : "";

  const seen = new Set<string>();
  const sensitivities: string[] = [];
  files.forEach((f) => {
    if (!seen.has(f.sensitivity)) {
      seen.add(f.sensitivity);
      sensitivities.push(f.sensitivity);
    }
  });

  return (
    `${actor} ${verb} ${noun} ${fileList} to ${recipient}${extraTail}; ` +
    `this exposed-to employee is untrusted for such content ` +
    `(${oxfordJoin(sensitivities)}).`
  );
};

export const formatRiskStory = (input: string | string[]): string => {
  const sentences = Array.isArray(input) ? input : splitSentences(input);
  const original = Array.isArray(input) ? input.join(" ") : input;
  if (sentences.length === 0) return original;

  let actorVerb: { actor: string; verb: string } | undefined;
  const byRecipient = new Map<string, ParsedFile[]>();

  for (const raw of sentences) {
    const s = raw.trim();
    if (!s) continue;
    if (!actorVerb && isActive(s)) actorVerb = matchActorVerb(s);

    const file = matchFile(s);
    const recipient = matchRecipient(s);
    const sensitivity = matchSensitivity(s);
    // Any sentence that doesn't fit the template -> bail out to the original text
    // so we never silently drop a risked file.
    if (!file || !recipient || !sensitivity) return original;

    const parsed: ParsedFile = {
      file,
      label: matchLabel(s),
      recipient,
      sensitivity,
    };
    const bucket = byRecipient.get(recipient);
    if (bucket) bucket.push(parsed);
    else byRecipient.set(recipient, [parsed]);
  }

  // Need an actor + verb (from an active sentence) to phrase the story.
  if (!actorVerb || byRecipient.size === 0) return original;

  const out: string[] = [];
  byRecipient.forEach((files, recipient) => {
    out.push(
      buildGroupSentence(actorVerb!.actor, actorVerb!.verb, recipient, files)
    );
  });
  return out.join(" ");
};
